import { useState, useEffect, useCallback, useRef } from 'react';
import { LeadService } from '../services';
import type { CheckoutLead, OnboardingLeadFilters, PaginationInfo } from '../types';

const SEARCH_DEBOUNCE_MS = 400;

function areArraysEqual<T>(left: T[] | undefined, right: T[] | undefined): boolean {
  const leftValues = left ?? [];
  const rightValues = right ?? [];

  if (leftValues.length !== rightValues.length) {
    return false;
  }

  return leftValues.every((value, index) => value === rightValues[index]);
}

function areOnboardingFiltersEqualExceptSearch(
  left: OnboardingLeadFilters,
  right: OnboardingLeadFilters,
): boolean {
  return areArraysEqual(left.classifications, right.classifications)
    && areArraysEqual(left.projectCodes, right.projectCodes)
    && left.hasPhotos === right.hasPhotos
    && left.hasParq === right.hasParq
    && left.hasAnamnesis === right.hasAnamnesis
    && left.joinedAtStart === right.joinedAtStart
    && left.joinedAtEnd === right.joinedAtEnd;
}

export interface UseOnboardingLeadsReturn {
  leads: CheckoutLead[];
  pagination: PaginationInfo;
  loading: boolean;
  error: string | null;
  refresh: (options?: { silent?: boolean }) => void;
  patchLeadOnboardingStatus: (updates: Record<number, Partial<CheckoutLead>>) => void;
  patchLead: (
    matcher: { studentId?: number | null; qualificationId?: number | null },
    data: Partial<CheckoutLead>,
  ) => void;
}

export function useOnboardingLeads(
  page: number = 1,
  pageSize: number = 10,
  filters: OnboardingLeadFilters = {},
): UseOnboardingLeadsReturn {
  const [leads, setLeads] = useState<CheckoutLead[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page,
    pageSize,
    totalRecords: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reqId = useRef(0);
  const silentReqId = useRef(0);

  const fetchPage = useCallback(
    async (
      requestedPage: number,
      requestedPageSize: number,
      serverFilters: OnboardingLeadFilters,
      options?: { silent?: boolean },
    ) => {
      const silent = Boolean(options?.silent);

      if (silent) {
        const snapshotReqId = reqId.current;
        const currentSilentReq = ++silentReqId.current;

        try {
          const response = await LeadService.listOnboardingPaginated(
            requestedPage,
            requestedPageSize,
            serverFilters,
          );

          if (currentSilentReq !== silentReqId.current) return;
          if (snapshotReqId !== reqId.current) return;

          setLeads(response.data);
          setPagination(response.pagination);
        } catch {
          // Silent refresh failures should not interrupt the onboarding screen.
        }

        return;
      }

      const currentReq = ++reqId.current;
      silentReqId.current += 1;

      try {
        setLoading(true);
        setError(null);

        const response = await LeadService.listOnboardingPaginated(
          requestedPage,
          requestedPageSize,
          serverFilters,
        );

        if (currentReq !== reqId.current) return;

        setLeads(response.data);
        setPagination(response.pagination);
      } catch (err: unknown) {
        if (currentReq !== reqId.current) return;
        const message = err instanceof Error ? err.message : 'Erro ao carregar onboarding';
        setError(message);
      } finally {
        if (currentReq === reqId.current) setLoading(false);
      }
    },
    [],
  );

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const prevFilters = useRef(filters);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    const currentFilters = filtersRef.current;
    const onlySearchChanged = currentFilters.searchName !== prevFilters.current.searchName
      && areOnboardingFiltersEqualExceptSearch(currentFilters, prevFilters.current);

    prevFilters.current = currentFilters;

    if (onlySearchChanged) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchPage(page, pageSize, currentFilters);
      }, SEARCH_DEBOUNCE_MS);
    } else {
      clearTimeout(debounceTimer.current);
      fetchPage(page, pageSize, currentFilters);
    }

    return () => clearTimeout(debounceTimer.current);
  }, [
    filters.searchName,
    filters.classifications,
    filters.projectCodes,
    filters.hasPhotos,
    filters.hasParq,
    filters.hasAnamnesis,
    filters.joinedAtStart,
    filters.joinedAtEnd,
    fetchPage,
    page,
    pageSize,
  ]);

  const refresh = useCallback((options?: { silent?: boolean }) => {
    fetchPage(page, pageSize, filters, options);
  }, [fetchPage, filters, page, pageSize]);

  const patchLeadOnboardingStatus = useCallback((updates: Record<number, Partial<CheckoutLead>>) => {
    setLeads((prev) => prev.map((lead) => {
      if (lead.student_id == null) {
        return lead;
      }

      const update = updates[lead.student_id];
      return update ? { ...lead, ...update } : lead;
    }));
  }, []);

  const patchLead = useCallback((
    matcher: { studentId?: number | null; qualificationId?: number | null },
    data: Partial<CheckoutLead>,
  ) => {
    setLeads((prev) => prev.map((lead) => {
      const matchesStudent = matcher.studentId != null
        && lead.student_id != null
        && lead.student_id === matcher.studentId;
      const matchesQualification = matcher.qualificationId != null
        && lead.qualification_id != null
        && lead.qualification_id === matcher.qualificationId;

      return matchesStudent || matchesQualification ? { ...lead, ...data } : lead;
    }));
  }, []);

  return {
    leads,
    pagination,
    loading,
    error,
    refresh,
    patchLeadOnboardingStatus,
    patchLead,
  };
}
